import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, Grid, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Skeleton, Stack,
  Tooltip, Typography,
} from '@mui/material';
import {
  Add, AutoFixHighOutlined, DeleteOutline, FreeBreakfastOutlined, ScheduleOutlined, CalendarMonthOutlined,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { timetableService, TimetableGrid, TimetableRow } from '../../../services/timetableService';
import EmptyState from '../../common/EmptyState';
import { SectionCard } from '../../common/SectionCard';
import { DAY_NAMES, DAY_SHORT, WEEK, fromMinutes, periodCount, toMinutes, weeklyPeriods } from '../timetableUtils';
import BellRow from './BellRow';
import { RulePicker } from './LightFields';
import QuickFillDialog from './QuickFillDialog';
import SchoolDayPreview from './SchoolDayPreview';

interface SchoolDayTabProps {
  sessionId: string;
  canWrite: boolean;
  /** Called after a save, so readiness checks can refresh. */
  onSaved: () => void;
}

const EMPTY: TimetableGrid = { days: [1, 2, 3, 4, 5], rows: [], shortDays: [] };
const AUTO_LABEL = /^Period \d+$/;

/** Number periods in order, leaving custom labels alone. */
const renumber = (rows: TimetableRow[]): TimetableRow[] => {
  let n = 0;
  return rows.map((r) => {
    if (r.kind !== 'period') return r;
    n += 1;
    return !r.label || AUTO_LABEL.test(r.label) ? { ...r, label: `Period ${n}` } : r;
  });
};

const shift = (rows: TimetableRow[], from: number, minutes: number) =>
  rows.map((r, i) => (i < from ? r : { ...r, start: fromMinutes(toMinutes(r.start) + minutes), end: fromMinutes(toMinutes(r.end) + minutes) }));

/** Mirrors the server's checks so a save never bounces. */
const rowProblems = (rows: TimetableRow[]): Record<number, string> => {
  const out: Record<number, string> = {};
  rows.forEach((r, i) => {
    if (toMinutes(r.end) <= toMinutes(r.start)) out[i] = 'Ends before it starts';
    else if (i > 0 && toMinutes(r.start) < toMinutes(rows[i - 1].end)) out[i] = 'Starts before the row above ends';
  });
  return out;
};

const normalize = (g: TimetableGrid): TimetableGrid => {
  const total = periodCount(g);
  return {
    days: [...g.days].sort((a, b) => a - b),
    rows: g.rows,
    shortDays: g.shortDays.filter((s) => g.days.includes(s.day) && s.periods < total),
  };
};

const same = (a: TimetableGrid, b: TimetableGrid) => JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));

/** The school day every timetable is built on: days, the bell schedule, and shorter days. */
const SchoolDayTab: React.FC<SchoolDayTabProps> = ({ sessionId, canWrite, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saved, setSaved] = useState<TimetableGrid | null>(null);
  const [draft, setDraft] = useState<TimetableGrid>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quickFill, setQuickFill] = useState(false);
  const [rowMenu, setRowMenu] = useState<{ anchor: HTMLElement; index: number } | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    timetableService.getConfig(sessionId)
      .then((cfg) => {
        if (!live) return;
        const grid = cfg ? { days: cfg.days, rows: cfg.rows, shortDays: cfg.shortDays } : null;
        setSaved(grid);
        setDraft(grid ?? EMPTY);
      })
      .catch((err) => live && enqueueSnackbar(err?.message || 'Could not load the school day', { variant: 'error' }))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [sessionId, enqueueSnackbar]);

  const problems = useMemo(() => rowProblems(draft.rows), [draft.rows]);
  const total = periodCount(draft);
  const dirty = !same(draft, saved ?? EMPTY) && draft.rows.length > 0;
  const invalid = !draft.days.length || !total || Object.keys(problems).length > 0;

  const setRows = useCallback((fn: (rows: TimetableRow[]) => TimetableRow[]) => setDraft((d) => ({ ...d, rows: renumber(fn(d.rows)) })), []);

  const toggleDay = (day: number) => setDraft((d) => ({ ...d, days: d.days.includes(day) ? d.days.filter((x) => x !== day) : [...d.days, day] }));

  /** Changing when a row ends moves every row after it by the same amount. */
  const setTime = useCallback((i: number, field: 'start' | 'end', value: string) => {
    if (!/^\d\d:\d\d$/.test(value)) return;
    setRows((rows) => {
      const next = rows.map((r, k) => (k === i ? { ...r, [field]: value } : r));
      return field === 'end' ? shift(next, i + 1, toMinutes(value) - toMinutes(rows[i].end)) : next;
    });
  }, [setRows]);

  const setLabel = useCallback((i: number, label: string) => setRows((rows) => rows.map((r, k) => (k === i ? { ...r, label } : r))), [setRows]);
  const openRowMenu = useCallback((anchor: HTMLElement, index: number) => setRowMenu({ anchor, index }), []);
  const periodNumbers = useMemo(() => {
    let n = 0;
    return draft.rows.map((r) => (r.kind === 'period' ? ++n : n));
  }, [draft.rows]);

  const insertAfter = (i: number, kind: 'period' | 'break') => setRows((rows) => {
    const lastPeriod = [...rows].reverse().find((r) => r.kind === 'period');
    const minutes = kind === 'break' ? 15 : lastPeriod ? toMinutes(lastPeriod.end) - toMinutes(lastPeriod.start) : 40;
    const at = i < 0 ? 0 : toMinutes(rows[i].end);
    const row: TimetableRow = { kind, label: kind === 'break' ? 'Break' : '', start: fromMinutes(at), end: fromMinutes(at + minutes) };
    return [...rows.slice(0, i + 1), row, ...shift(rows.slice(i + 1), 0, minutes)];
  });

  const remove = (i: number) => setRows((rows) => {
    const minutes = toMinutes(rows[i].end) - toMinutes(rows[i].start);
    return [...rows.slice(0, i), ...shift(rows.slice(i + 1), 0, -minutes)];
  });

  const setShort = (day: number, value: string) => setDraft((d) => ({
    ...d,
    shortDays: [...d.shortDays.filter((s) => s.day !== day), ...(Number(value) < periodCount(d) ? [{ day, periods: Number(value) }] : [])],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const grid = normalize(draft);
      const cfg = await timetableService.saveConfig(sessionId, grid);
      const next = { days: cfg.days, rows: cfg.rows, shortDays: cfg.shortDays };
      setSaved(next);
      setDraft(next);
      enqueueSnackbar('School day saved', { variant: 'success' });
      onSaved();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not save the school day', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const shortOptions = useMemo(
    () => Array.from({ length: total }, (_, k) => total - k).map((n) => ({ value: String(n), label: n === total ? `Full day (${n} periods)` : `Ends after period ${n}` })),
    [total],
  );

  if (loading) {
    return (
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}><Skeleton variant="rounded" height={420} /></Grid>
        <Grid size={{ xs: 12, md: 5 }}><Skeleton variant="rounded" height={260} /></Grid>
      </Grid>
    );
  }

  if (!draft.rows.length) {
    return (
      <>
        <EmptyState
          icon={<ScheduleOutlined />}
          title="Set up the school day"
          description="Choose the days school runs and the bell schedule: when each period starts and ends, and where the breaks fall. Every timetable is built on it."
          action={canWrite ? { label: 'Quick fill', icon: <AutoFixHighOutlined />, onClick: () => setQuickFill(true) } : undefined}
          secondaryAction={canWrite ? { label: 'Start from scratch', onClick: () => insertAfter(-1, 'period') } : undefined}
        />
        <QuickFillDialog open={quickFill} replacing={false} onClose={() => setQuickFill(false)} onApply={(rows) => { setDraft((d) => ({ ...d, rows })); setQuickFill(false); }} />
      </>
    );
  }

  const readOnly = !canWrite;

  return (
    <Box sx={{ pb: dirty ? 9 : 0 }}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={2.5}>
            <SectionCard title="School days">
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {WEEK.map((d) => {
                  const on = draft.days.includes(d);
                  return (
                    <Chip
                      key={d}
                      label={DAY_SHORT[d]}
                      color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      onClick={readOnly ? undefined : () => toggleDay(d)}
                      sx={{ minWidth: 56, fontWeight: 600 }}
                    />
                  );
                })}
              </Stack>
              {!draft.days.length && <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>Pick at least one day.</Typography>}
            </SectionCard>

            <SectionCard
              title="Bell schedule"
              action={canWrite && (
                <Button size="small" startIcon={<AutoFixHighOutlined />} onClick={() => setQuickFill(true)} sx={{ textTransform: 'none' }}>Quick fill</Button>
              )}
            >
              <Stack spacing={0.75}>
                {draft.rows.map((row, i) => (
                  <BellRow
                    key={i}
                    row={row}
                    index={i}
                    number={row.kind === 'period' ? periodNumbers[i] : null}
                    problem={problems[i]}
                    readOnly={readOnly}
                    canWrite={canWrite}
                    onLabel={setLabel}
                    onTime={setTime}
                    onMenu={openRowMenu}
                  />
                ))}
              </Stack>
              {canWrite && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                  <Button size="small" startIcon={<Add />} onClick={() => insertAfter(draft.rows.length - 1, 'period')} sx={{ textTransform: 'none' }}>Add period</Button>
                  <Button size="small" startIcon={<FreeBreakfastOutlined />} color="inherit" onClick={() => insertAfter(draft.rows.length - 1, 'break')} sx={{ textTransform: 'none', color: 'text.secondary' }}>Add break</Button>
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto !important' }}>Changing when a row ends moves the rows after it.</Typography>
                </Stack>
              )}
            </SectionCard>

            {total > 1 && draft.days.length > 0 && (
              <SectionCard title="Shorter days">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>A day can end early, for example Friday after period 5.</Typography>
                <Stack spacing={1}>
                  {WEEK.filter((d) => draft.days.includes(d)).map((d) => {
                    const short = draft.shortDays.find((s) => s.day === d);
                    return (
                      <Stack key={d} direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body2" sx={{ width: 96, fontWeight: 500 }}>{DAY_NAMES[d]}</Typography>
                        <Box sx={{ width: 240 }}>
                          <RulePicker
                            label={`${DAY_NAMES[d]} length`}
                            value={String(short && short.periods < total ? short.periods : total)}
                            onChange={(v) => setShort(d, v)}
                            options={shortOptions}
                            disabled={readOnly}
                          />
                        </Box>
                      </Stack>
                    );
                  })}
                </Stack>
              </SectionCard>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Box sx={{ position: { md: 'sticky' }, top: 88 }}>
            <SectionCard title="Preview" action={<Typography variant="caption" color="text.secondary">{weeklyPeriods(draft)} periods a week</Typography>}>
              <SchoolDayPreview grid={draft} />
            </SectionCard>
          </Box>
        </Grid>
      </Grid>

      <Menu anchorEl={rowMenu?.anchor} open={!!rowMenu} onClose={() => setRowMenu(null)}>
        <MenuItem onClick={() => { insertAfter(rowMenu!.index, 'period'); setRowMenu(null); }}>
          <ListItemIcon><Add fontSize="small" /></ListItemIcon><ListItemText>Insert period after</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { insertAfter(rowMenu!.index, 'break'); setRowMenu(null); }}>
          <ListItemIcon><FreeBreakfastOutlined fontSize="small" /></ListItemIcon><ListItemText>Insert break after</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { remove(rowMenu!.index); setRowMenu(null); }} sx={{ color: 'error.main' }} disabled={draft.rows.length === 1}>
          <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon><ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>

      <QuickFillDialog
        open={quickFill}
        replacing={draft.rows.length > 0}
        onClose={() => setQuickFill(false)}
        onApply={(rows) => { setDraft((d) => ({ ...d, rows, shortDays: [] })); setQuickFill(false); }}
      />

      {dirty && canWrite && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 10,
            display: 'flex', alignItems: 'center', gap: 2, pl: 2.5, pr: 1.25, py: 1, borderRadius: '14px',
          }}
        >
          <CalendarMonthOutlined fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600}>Unsaved changes</Typography>
          <Tooltip title={invalid ? 'Fix the highlighted rows first' : ''}>
            <span style={{ display: 'flex', gap: 8 }}>
              <Button color="inherit" onClick={() => setDraft(saved ?? EMPTY)} disabled={saving}>Discard</Button>
              <Button variant="contained" onClick={save} disabled={saving || invalid}>{saving ? 'Saving' : 'Save'}</Button>
            </span>
          </Tooltip>
        </Paper>
      )}
    </Box>
  );
};

export default SchoolDayTab;
